import type { SdkDocEntry } from "./types";

/** Full markdown bodies keyed by doc id. */
export const SDK_DOC_ENTRIES: Record<string, SdkDocEntry> = {
  "intro-overview": {
    id: "intro-overview",
    title: "Vybe Indicators SDK",
    summary: "C# SDK for user-authored custom indicators in Vybe Studio.",
    markdown: `# Vybe Indicators SDK

The **Vybe Indicators SDK** (\`Vybe.Indicators\`) is how you author custom chart indicators in **Vybe Studio**. Indicators are multi-file **C# projects** compiled in the browser with Roslyn inside a WebAssembly runtime, then plotted on KLineCharts.

> This SDK is **not** the Vybe **terminal DSL** (\`Chart.*\`, \`vybe_dsl\`). The terminal DSL controls the trading client UI; the SDK is for writing indicator logic.

## Requirements

- \`using Vybe.Indicators;\`
- A public class inheriting \`Indicator\`
- \`[Indicator(Name = "...", ...)]\` on the class
- Override \`OnInit()\`, \`OnCalculate(int index)\`, and optionally \`OnDeinit()\`

## Project layout

| File | Purpose |
|------|---------|
| \`Indicator.cs\` | Entry source (default) |
| \`Indicator.csproj\` | Vybe MSBuild metadata |
| \`assets/icon.svg\` | Marketplace-style icon |
| \`docs/\` | Your own notes (optional) |

Compile in the editor (**Compile**), then add \`custom_<projectId>\` to the chart. Compile results are **session-scoped** until you compile again.`,
  },

  "intro-vs-dsl": {
    id: "intro-vs-dsl",
    title: "SDK vs Terminal DSL",
    summary: "Custom indicators use C# SDK; terminal commands use vybe_dsl.",
    markdown: `# SDK vs Terminal DSL

| | **Vybe Indicators SDK** | **Terminal DSL** |
|---|-------------------------|------------------|
| Language | C# (\`Vybe.Indicators\`) | Vybe script commands |
| Where | Vybe Studio editor | Client terminal / agent |
| Purpose | Indicator math & plots | Chart control, analytics, trading UI |
| Compile | Roslyn → WASM | Interpreted on client |

Do not use \`Chart.Indicators.*\` terminal commands as the custom-indicator API.`,
  },

  "project-csproj": {
    id: "project-csproj",
    title: "Indicator.csproj",
    summary: "Vybe MSBuild properties: name, overlay, entry file, icon.",
    markdown: `# Indicator.csproj

Vybe-specific MSBuild properties (parsed at compile time):

| Property | Example | Notes |
|----------|---------|-------|
| \`VybeIndicatorName\` | My MA | Display name |
| \`VybeProjectKind\` | Indicator | \`Indicator\` or \`TradingAlgo\` (algo execution not supported yet) |
| \`VybeEntryFile\` | Indicator.cs | Main \`.cs\` file |
| \`VybeIconFile\` | assets/icon.svg | Icon asset path |
| \`VybeOverlay\` | true | Plot on price pane vs separate pane |

\`TradingAlgo\` projects compile but **init/calculate are rejected** at runtime.`,
  },

  "lifecycle-oninit": {
    id: "lifecycle-oninit",
    title: "OnInit()",
    summary: "Called once when the indicator instance is attached.",
    markdown: `# OnInit()

\`\`\`csharp
protected override void OnInit()
{
    Logger.Print("Indicator started");
}
\`\`\`

Use \`OnInit()\` for one-time setup: log messages, validate inputs, or initialize private fields. The runtime wires OHLCV \`Series\` before the first \`OnCalculate\` pass.

Pair with \`OnDeinit()\` for cleanup when the instance is removed.`,
  },

  "lifecycle-oncalculate": {
    id: "lifecycle-oncalculate",
    title: "OnCalculate(int index)",
    summary: "Called per bar; index 0 = oldest, n-1 = newest.",
    markdown: `# OnCalculate(int index)

\`\`\`csharp
protected override void OnCalculate(int index)
{
    if (index < Period - 1) return;
    Ma[index] = Sma(Close, Period, index);
}
\`\`\`

## Bar indexing

- **\`index == 0\`** → oldest bar in the current buffer
- **\`index == Bars - 1\`** → newest (forming) bar
- \`Close[index - 1]\` is the **previous** bar in time

This matches MQL5-style loops managed by the runtime (not Pine Script’s \`[0]\` = last bar).

## Incremental passes

The client passes \`PrevCalculated\` and may run only from \`startIndex\` on live updates. Reseed rolling state when \`index == PrevCalculated - 1\` so tail recalculations stay correct.

Keep \`OnCalculate\` **O(1) amortized** per bar using running sums or private state.`,
  },

  "lifecycle-ondeinit": {
    id: "lifecycle-ondeinit",
    title: "OnDeinit()",
    summary: "Cleanup when the indicator instance is removed.",
    markdown: `# OnDeinit()

\`\`\`csharp
protected override void OnDeinit()
{
    Logger.Print("Indicator removed");
}
\`\`\`

Called when the chart removes the indicator instance or the editor preview reloads. Release handles or log diagnostics here.`,
  },

  "lifecycle-prevcalculated": {
    id: "lifecycle-prevcalculated",
    title: "PrevCalculated & RatesTotal",
    summary: "MQL5-style incremental calculate support.",
    markdown: `# PrevCalculated & RatesTotal

| Member | Meaning |
|--------|---------|
| \`Bars\` / \`RatesTotal\` | Bar count in the current pass |
| \`PrevCalculated\` | Bars computed on the **previous** pass (set by runtime) |

## Client → WASM modes

| Change | Mode | Start index |
|--------|------|-------------|
| Unchanged OHLC | skip | — |
| Last bar tick | incremental | \`n - 1\` |
| New bar | incremental | \`prevCalculated - 1\` |
| History prepend / input change | full | \`0\` |

**Reseed pattern** (from default Trend Dashboard):

\`\`\`csharp
if (index == PrevCalculated - 1)
{
    // Rebuild rolling state from index 0..index before continuing
}
\`\`\``,
  },

  "type-indicator": {
    id: "type-indicator",
    title: "Indicator (base class)",
    summary: "Abstract base with OHLCV, lifecycle, and helpers.",
    markdown: `# Indicator

\`\`\`csharp
public abstract class Indicator
{
    public Series Open { get; set; }
    public Series High { get; set; }
    public Series Low { get; set; }
    public Series Close { get; set; }
    public Series Volume { get; set; }

    public int Bars { get; }
    public int PrevCalculated { get; }
    public int RatesTotal => Bars;

    protected virtual void OnInit() { }
    protected virtual void OnCalculate(int index) { }
    protected virtual void OnDeinit() { }

    protected static double Sma(Series series, int period, int index);
    protected static double GetPrice(Series close, int index);
    protected static double HistogramBarStyleIndex(
        double current, double previous, bool hollowBodiesEnabled);
}
\`\`\`

There is **no** \`Draw.*\`, \`Math.RSI\`, or MTF API in the SDK — use attributes + \`Series\` state buffers for overlays.`,
  },

  "type-series": {
    id: "type-series",
    title: "Series",
    summary: "Per-bar double buffer for plots and companion state.",
    markdown: `# Series

\`\`\`csharp
[Plot(Label = "MA", Color = "#2962FF")]
public Series Ma { get; set; } = null!;

protected override void OnCalculate(int index)
{
    Ma[index] = someValue;
    // Hide plot for this bar:
    Ma[index] = double.NaN;
}
\`\`\`

- \`Series.NaN\` → skip rendering that bar
- Index with \`[Plot]\`, \`[CandleColor]\`, \`[Signal]\`, etc. must be type \`Series\`
- Companion series (color index, signal state) do **not** use \`[Plot]\``,
  },

  "type-color": {
    id: "type-color",
    title: "Color",
    summary: "Hex color struct for inputs and plot styling.",
    markdown: `# Color

\`\`\`csharp
[Input(Label = "Line color")]
public Color LineColor { get; set; } = "#2962FF";

[Plot(ColorInput = "LineColor")]
public Series Ma { get; set; } = null!;
\`\`\`

Implicit conversion from \`string\` hex allows \`public Color X { get; set; } = "#2962FF";\`. The client renders a native color picker for \`Color\` inputs.`,
  },

  "type-logger": {
    id: "type-logger",
    title: "Logger",
    summary: "Print messages to the editor Logger tab.",
    markdown: `# Logger

\`\`\`csharp
Logger.Print("Hello from OnInit");
\`\`\`

Messages appear in the Vybe Studio **Logger** terminal tab after preview/chart init. Empty strings are ignored.`,
  },

  "attr-indicator": {
    id: "attr-indicator",
    title: "[Indicator]",
    summary: "Class metadata: name, version, overlay, description.",
    markdown: `# [Indicator]

\`\`\`csharp
[Indicator(
    Name = "Trend Dashboard",
    Version = "1.0",
    Overlay = true,
    Description = "Multi-pane showcase")]
public class TrendDashboard : Indicator { }
\`\`\`

| Property | Notes |
|----------|-------|
| \`Name\` | Required for validation |
| \`Overlay\` | \`true\` = main price pane; \`false\` = own pane |
| \`Version\` | Shown in UI / compile metadata |`,
  },

  "attr-input": {
    id: "attr-input",
    title: "[Input]",
    summary: "User settings: int, double, bool, Color, string.",
    markdown: `# [Input]

\`\`\`csharp
[Input(Label = "Period", Group = "MA", Min = 2, Max = 500, Step = 1)]
public int Period { get; set; } = 14;

[Input(Label = "Show MA", Group = "Display")]
public bool ShowMa { get; set; } = true;
\`\`\`

**Supported WASM types:** \`bool\`, \`Color\`, \`string\`, numeric (\`int\`/\`long\`/\`double\`/\`float\`).

Values are passed from chart \`settings.values\` into \`ComputeIndicator\`. Use bool inputs to toggle visibility without clearing buffers.`,
  },

  "attr-plot": {
    id: "attr-plot",
    title: "[Plot]",
    summary: "Line, histogram, dashed plots and pane targeting.",
    markdown: `# [Plot]

\`\`\`csharp
[Plot(Label = "MACD", Style = "Histogram", BaseValue = 0, Pane = "Momentum")]
public Series MacdHist { get; set; } = null!;
\`\`\`

| Property | Notes |
|----------|-------|
| \`Style\` | \`Line\`, \`Dashed\`, \`Dotted\`, \`Histogram\`, \`Bar\`, \`ColorLine\`, \`ColorHistogram\` |
| \`Pane\` | Empty / \`Main\` / \`overlay\` = base pane; other names → sub-pane |
| \`BaseValue\` | e.g. \`0\` for MACD histogram baseline |
| \`Color\` / \`ColorInput\` / \`Colors\` / \`ColorInputs\` | Static or input-driven colors |`,
  },

  "attr-pane": {
    id: "attr-pane",
    title: "[Pane]",
    summary: "Sub-pane height and scale (class-level, multiple allowed).",
    markdown: `# [Pane]

\`\`\`csharp
[Pane(Name = "Momentum", Height = 120, MinValue = -2, MaxValue = 2)]
[Pane(Name = "Volume", Height = 80)]
public class TrendDashboard : Indicator { }
\`\`\`

Declare once per named pane. Plots with matching \`[Plot(Pane = "Momentum")]\` render in that sub-window. One WASM compute pass feeds all panes.`,
  },

  "attr-plot-color-index": {
    id: "attr-plot-color-index",
    title: "[PlotColorIndex]",
    summary: "Per-bar palette index for colored lines.",
    markdown: `# [PlotColorIndex]

\`\`\`csharp
[Plot(Label = "MA", Colors = "#26A69A,#FF6D00", ColorInputs = "UpColor,DownColor")]
public Series Ma { get; set; } = null!;

[PlotColorIndex(Plot = "Ma")]
public Series MaColor { get; set; } = null!;

protected override void OnCalculate(int index)
{
    Ma[index] = value;
    MaColor[index] = Close[index] >= Open[index] ? 0 : 1;
}
\`\`\`

Set companion index \`0\`, \`1\`, … per bar. Use \`double.NaN\` on the main plot to hide a bar.`,
  },

  "attr-plot-bar-style": {
    id: "attr-plot-bar-style",
    title: "[PlotBarStyle]",
    summary: "Hollow vs filled histogram bodies.",
    markdown: `# [PlotBarStyle]

\`\`\`csharp
[Plot(Label = "Hist", Style = "Histogram", BaseValue = 0)]
public Series MacdHist { get; set; } = null!;

[PlotBarStyle(Plot = "MacdHist")]
public Series MacdHistStyle { get; set; } = null!;

// In OnCalculate:
MacdHistStyle[index] = HistogramBarStyleIndex(
    MacdHist[index], MacdHist[index - 1], HollowBodies);
\`\`\`

\`0\` = filled body, \`1\` = hollow (stroke only).`,
  },

  "attr-candle-color": {
    id: "attr-candle-color",
    title: "[CandleColor]",
    summary: "Per-bar candle body/border/wick recolor (overlay).",
    markdown: `# [CandleColor]

Requires **overlay** indicator on a **candlestick** chart.

\`\`\`csharp
[CandleColor(
    BodyColorInputs = "UpBody,DownBody,NeutralBody",
    BodyColors = "#26A69A,#EF5350,#787B86")]
public Series CandleState { get; set; } = null!;

protected override void OnCalculate(int index)
{
    CandleState[index] = trendUp ? 0 : trendDown ? 1 : 2;
    // Skip: CandleState[index] = -1;
}
\`\`\`

State \`0\`, \`1\`, … maps to palette entries; \`-1\` or \`NaN\` = no recolor.`,
  },

  "attr-signal": {
    id: "attr-signal",
    title: "[Signal]",
    summary: "Buy/sell arrows and optional labels on main pane.",
    markdown: `# [Signal]

\`\`\`csharp
[Signal(BuyText = "BUY", SellText = "SELL", ShowInput = "ShowSignals")]
public Series SignalState { get; set; } = null!;

protected override void OnCalculate(int index)
{
    SignalState[index] = buyCondition ? 0 : sellCondition ? 1 : -1;
}
\`\`\`

| Value | Meaning |
|-------|---------|
| \`0\` | Buy arrow |
| \`1\` | Sell arrow |
| \`-1\` / \`NaN\` | None |

Multiple \`[Signal]\` members on one class are supported.`,
  },

  "attr-zone": {
    id: "attr-zone",
    title: "[Zone]",
    summary: "Supply/demand zone boxes on main pane.",
    markdown: `# [Zone]

\`\`\`csharp
public Series ZoneTop { get; set; } = null!;
public Series ZoneBottom { get; set; } = null!;

[Zone(Top = "ZoneTop", Bottom = "ZoneBottom", ShowInput = "ShowZones")]
public Series ZoneState { get; set; } = null!;
\`\`\`

| State | Meaning |
|-------|---------|
| \`0\` | Demand |
| \`1\` | Supply |
| \`2\` | Broken |
| \`-1\` / \`NaN\` | None |

Set top/bottom prices per bar; carry forward until broken.`,
  },

  "attr-price-histogram": {
    id: "attr-price-histogram",
    title: "[PriceHistogram]",
    summary: "Volume-at-price profile and heatmap.",
    markdown: `# [PriceHistogram]

\`\`\`csharp
public Series BuyVol { get; set; } = null!;
public Series SellVol { get; set; } = null!;

[PriceHistogram(Buy = "BuyVol", Sell = "SellVol", ShowProfileInput = "ShowProfile")]
public Series PriceHistState { get; set; } = null!;

protected override void OnCalculate(int index)
{
    BuyVol[index] = Close[index] >= Open[index] ? Volume[index] : 0;
    SellVol[index] = Close[index] < Open[index] ? Volume[index] : 0;
}
\`\`\`

Splits per-bar volume for right-side profile and background heatmap bands.`,
  },

  "attr-dashboard": {
    id: "attr-dashboard",
    title: "[Dashboard] & [DashboardCell]",
    summary: "On-chart stats table overlay.",
    markdown: `# [Dashboard] & [DashboardCell]

\`\`\`csharp
[Dashboard(Title = "Trend", Position = "TopRight", ShowInput = "ShowDash")]
public class MyIndicator : Indicator { }

[DashboardCell(Label = "Trend", Row = 0, Mode = "State", Texts = "Up,Down,Flat")]
public Series DashTrend { get; set; } = null!;
\`\`\`

- \`Mode = "State"\` → index maps to \`Texts\` / colors
- \`Mode = "Numeric"\` → format with \`Decimals\`, \`Prefix\`, \`Suffix\`
- Cells use \`[DashboardCell]\` only (no \`[Plot]\`)`,
  },

  "best-incremental": {
    id: "best-incremental",
    title: "Incremental calculate",
    summary: "O(1) per bar; reseed at PrevCalculated - 1.",
    markdown: `# Best practice: incremental calculate

1. Use **rolling sums** or private fields instead of scanning \`0..index\` every bar.
2. On \`index == PrevCalculated - 1\`, reseed state from the prior bar.
3. Write \`double.NaN\` to hide plots; \`-1\` on state buffers to skip overlays.
4. Do not clear volume buffers during warm-up if other series skip early.

See \`default-indicator-code.ts\` (Trend Dashboard) for O(1) SMA/EMA patterns.`,
  },

  "best-compile": {
    id: "best-compile",
    title: "Compile & session",
    summary: "Compile in editor; results are in-memory until reload.",
    markdown: `# Compile workflow

1. **Edit** \`.cs\` sources in the Files navigator.
2. **Compile** → Roslyn in WASM; Output tab shows diagnostics.
3. **Preview** chart reloads on compile revision + saved inputs.
4. **Save** → IndexedDB project (validation should pass entry file checks).
5. **Main chart** needs a compile in **this browser session** (or hydrate from persisted build).

\`useIndicatorCompileStore\` is **not** written to IndexedDB. After full page reload, compile again.

Icon assets embed as \`VybeIndicatorIcon\` manifest resource when present.`,
  },

  "example-minimal-ma": {
    id: "example-minimal-ma",
    title: "Example: minimal MA",
    summary: "Single overlay line with Period input.",
    markdown: `# Example: minimal MA

\`\`\`csharp
using Vybe.Indicators;

[Indicator(Name = "Simple MA", Overlay = true)]
public class SimpleMa : Indicator
{
    [Input(Label = "Period", Min = 2, Max = 500)]
    public int Period { get; set; } = 14;

    [Plot(Label = "MA", Color = "#2962FF")]
    public Series Ma { get; set; } = null!;

    protected override void OnCalculate(int index)
    {
        if (index < Period - 1)
        {
            Ma[index] = double.NaN;
            return;
        }
        Ma[index] = Sma(Close, Period, index);
    }
}
\`\`\`

Use project templates in Vybe Studio for RSI, MACD, Bollinger, and full Trend Dashboard samples.`,
  },
};
